// Pure engine for members' Golf Game Central SEASON mode. Accumulates points
// across rounds for one of two preset season games, computes running standings
// (teams + combined sides), and settles the season pot. No DB, no React — fully
// unit-testable. Team Cup reuses the self-contained match engine in ggTeamCup
// (never rebuilt). Stableford uses the STANDARD points formula — identical to
// ggFormats.stablefordPoints standard mode (max(0, 2 − vsPar)) — kept inline so
// this engine stays pure/testable under `node --test`: importing ggFormats
// drags in club.js → siteMode.js which read import.meta.env (vite-only) and
// crash the plain-node test runner (the existing gg tests hit the same wall).
import { allocateStrokes, netForHole, computeTeamMatchResults, DEFAULT_STROKE_INDEX } from './ggTeamCup.js'

// Standard Stableford points for a hole's (net) score vs par — eagle 4 /
// birdie 3 / par 2 / bogey 1 / double+ 0. Mirrors ggFormats.stablefordPoints.
export const stablefordPoints = (vsPar) => Math.max(0, 2 - vsPar)

// Standard par-72 fallback when a round carries no course pars.
export const DEFAULT_PARS = [4, 5, 4, 3, 4, 4, 3, 5, 4, 4, 4, 3, 5, 4, 4, 3, 4, 5]
export const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100

// { playerId: courseHandicap } across every team's players.
function courseHandicaps(teams) {
  const out = {}
  for (const t of teams || []) for (const p of t.players || []) {
    if (p && p.playerId != null) out[p.playerId] = Number(p.ch) || 0
  }
  return out
}

// { playerId: strokes[18] } — reuses ggTeamCup.allocateStrokes.
function strokesMap(teams, strokeIndex) {
  const out = {}
  for (const [pid, ch] of Object.entries(courseHandicaps(teams))) out[pid] = allocateStrokes(ch, strokeIndex)
  return out
}

// ── Team Stableford (2-man best-ball) for ONE round → { teamId: points } ─────
// Per hole per team = MAX of the two players' NET Stableford points (best-ball),
// standard scoring via ggFormats.stablefordPoints. Sum of 18 = round points.
export function stablefordRoundPoints(teams, round) {
  const pars = round?.pars || DEFAULT_PARS
  const si = round?.strokeIndex || DEFAULT_STROKE_INDEX
  const net = round?.net !== false // net ON by default (handicaps)
  const strokes = strokesMap(teams, si)
  const out = {}
  for (const t of teams || []) {
    let pts = 0
    for (let h = 1; h <= 18; h++) {
      const par = pars[h - 1] || 4
      let best = null
      for (const p of t.players || []) {
        const gross = Number(round?.scoresByHole?.[h]?.[p.playerId])
        if (!Number.isFinite(gross) || gross <= 0) continue
        const score = net ? netForHole(gross, strokes[p.playerId]?.[h - 1] ?? 0) : gross
        const sp = stablefordPoints(score - par) // standard: max(0, 2 − vsPar)
        if (best == null || sp > best) best = sp
      }
      if (best != null) pts += best
    }
    out[t.id] = pts
  }
  return out
}

// ── Team Cup for ONE round → { teamId: points } via the existing engine ──────
export function teamCupRoundPoints(teams, round, scoringSystem = 'official') {
  const si = round?.strokeIndex || DEFAULT_STROKE_INDEX
  const strokesByPlayer = strokesMap(teams, si)
  const engineTeams = (teams || []).map((t) => ({ id: t.id, playerIds: (t.players || []).map((p) => p.playerId) }))
  const r = computeTeamMatchResults({
    teams: engineTeams,
    scoresByHole: round?.scoresByHole || {},
    strokesByPlayer,
    handicapsOn: round?.net !== false,
    scoringSystem,
  })
  return r.teamPoints
}

// Dispatch one round to the right scorer by season type.
export function roundPoints(season, round) {
  return season?.seasonType === 'team-cup'
    ? teamCupRoundPoints(season.teams, round, season.scoringSystem || 'official')
    : stablefordRoundPoints(season.teams, round)
}

// ── Running season standings: accumulate every round; combine sides ──────────
export function seasonStandings(season) {
  const teams = season?.teams || []
  const rounds = season?.rounds || []
  const totals = {}
  teams.forEach((t) => { totals[t.id] = 0 })
  const perRound = rounds.map((rd) => {
    const rp = roundPoints(season, rd)
    teams.forEach((t) => { totals[t.id] += Number(rp[t.id]) || 0 })
    return rp
  })
  const teamRows = teams
    .map((t) => ({ teamId: t.id, name: t.name || t.id, total: round2(totals[t.id]) }))
    .sort((a, b) => b.total - a.total)
    .map((r, i) => ({ ...r, place: i + 1 }))
  // Sides = sum of member-team totals (combine 2-man teams into bigger sides).
  const sides = (season?.sides || [])
    .map((s) => ({
      sideId: s.sideId,
      name: s.name || s.sideId,
      total: round2((s.teamIds || []).reduce((sum, tid) => sum + (totals[tid] || 0), 0)),
    }))
    .sort((a, b) => b.total - a.total)
    .map((r, i) => ({ ...r, place: i + 1 }))
  return { teamRows, sides, perRound, totals }
}

// ── Season pot payout (winner-take-all / top-N split), zero-sum ──────────────
// pot: { total, payout:'winner'|'topN', n?, weights?[], useSides? }. Payouts
// always sum EXACTLY to pot.total (residual folded into the top payout), so the
// pot is fully distributed. Ties at a paid place pool and split their slices.
export function potPayout(standings, pot) {
  const useSides = !!pot?.useSides && (standings?.sides?.length > 0)
  const src = (useSides ? standings.sides : standings?.teamRows) || []
  const total = Number(pot?.total) || 0
  const rows = src.map((r) => ({ id: r.teamId || r.sideId, name: r.name, total: r.total }))
  if (total <= 0 || rows.length === 0) return { payouts: [], total: 0 }
  const sorted = [...rows].sort((a, b) => b.total - a.total)
  const mode = pot?.payout === 'topN' ? 'topN' : 'winner'
  const places = mode === 'winner' ? 1 : Math.min(Math.max(Number(pot?.n) || 3, 1), sorted.length)
  const weights = mode === 'winner' ? [1]
    : (pot?.weights?.length ? pot.weights.slice(0, places) : Array(places).fill(1))
  const wsum = weights.reduce((a, b) => a + b, 0) || 1
  const slices = weights.map((w) => total * (w / wsum))

  const payById = {}
  let pos = 0
  let i = 0
  while (i < sorted.length && pos < places) {
    let j = i
    while (j + 1 < sorted.length && sorted[j + 1].total === sorted[i].total) j++
    const groupSize = j - i + 1
    const covered = Math.min(pos + groupSize, places) - pos
    const pooled = slices.slice(pos, pos + covered).reduce((a, b) => a + b, 0)
    const each = pooled / groupSize
    for (let k = i; k <= j; k++) payById[sorted[k].id] = (payById[sorted[k].id] || 0) + each
    pos += groupSize
    i = j + 1
  }
  let payouts = Object.entries(payById)
    .map(([id, amt]) => ({ id, name: (sorted.find((r) => r.id === id) || {}).name, amount: round2(amt) }))
    .sort((a, b) => b.amount - a.amount)
  const paid = round2(payouts.reduce((a, p) => a + p.amount, 0))
  const residual = round2(total - paid)
  if (payouts.length && Math.abs(residual) >= 0.01) payouts[0].amount = round2(payouts[0].amount + residual)
  return { payouts, total }
}
